import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  getAdminCompanies,
  getAdminCompanyUsers,
  postAdminCompany,
  postAdminUserSharedAccount,
} from '../../../api';
import * as colors from '../../../util/colors';
import AdminUserPicker, {
  formatAdminUserAmount,
  formatAdminUserName,
} from '../../common/AdminUserPicker';
import Button from '../../common/Button';
import {ErrorAlert} from '../../common/Alerts';
import Paper from '../../common/Paper';

const DEFAULT_EMAIL = 'company@drivechargers.ru';
const DEFAULT_FIRST_NAME = 'Компания';

const getErrorMessage = async (response, fallback) => {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch (e) {
    return fallback;
  }
};

function SharedAccountsTab({authorization}) {
  const [email, setEmail] = React.useState(DEFAULT_EMAIL);
  const [firstName, setFirstName] = React.useState(DEFAULT_FIRST_NAME);
  const [lastName, setLastName] = React.useState('');
  const [companySubmitting, setCompanySubmitting] = React.useState(false);
  const [companyError, setCompanyError] = React.useState('');
  const [companySuccess, setCompanySuccess] = React.useState('');
  const [createdCompany, setCreatedCompany] = React.useState(null);

  const handleCreateCompany = async (event) => {
    event.preventDefault();

    const normalizedEmail = email.trim();
    const normalizedFirstName = firstName.trim();
    const normalizedLastName = lastName.trim();

    if (!normalizedEmail || !normalizedFirstName || !normalizedLastName) {
      setCompanyError('Заполните email, имя и название компании');
      setCompanySuccess('');
      return;
    }

    setCompanySubmitting(true);
    setCompanyError('');
    setCompanySuccess('');

    try {
      const response = await postAdminCompany({authorization}, {
        email: normalizedEmail,
        first_name: normalizedFirstName,
        last_name: normalizedLastName,
      });

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось создать компанию'));
      }

      const company = await response.json();
      setCreatedCompany({...company, users_count: 0});
      setLastName('');
      setCompanySuccess(`Компания «${formatAdminUserName(company)}» создана и добавлена в список.`);
    } catch (error) {
      setCompanyError(error.message || 'Не удалось создать компанию');
    } finally {
      setCompanySubmitting(false);
    }
  };

  return (
    <Paper>
      <PageHeader>
        <Heading>Корпоративные счета</Heading>
        <Description>
          Компания хранится как пользователь с ролью <Code>company</Code>. Ее личный счет
          используется как общий баланс привязанных пользователей.
        </Description>
      </PageHeader>

      <SectionsGrid>
        <SectionCard>
          <SectionHeader>
            <SectionNumber>1</SectionNumber>
            <div>
              <SectionTitle>Создать компанию</SectionTitle>
              <SectionDescription>
                После создания компания добавится в каталог и автоматически раскроется.
              </SectionDescription>
            </div>
          </SectionHeader>

          <Form onSubmit={handleCreateCompany}>
            {companyError && <ErrorAlert noTriangle>{companyError}</ErrorAlert>}
            {companySuccess && <SuccessAlert>{companySuccess}</SuccessAlert>}

            <FieldGroup>
              <FieldLabel htmlFor="company-email">Email</FieldLabel>
              <FieldInput
                id="company-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setCompanyError('');
                  setCompanySuccess('');
                }}
              />
              <HelperText>Email должен быть уникальным для каждой компании.</HelperText>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel htmlFor="company-first-name">Имя</FieldLabel>
              <FieldInput
                id="company-first-name"
                type="text"
                value={firstName}
                onChange={(event) => {
                  setFirstName(event.target.value);
                  setCompanyError('');
                  setCompanySuccess('');
                }}
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel htmlFor="company-last-name">Название компании</FieldLabel>
              <FieldInput
                id="company-last-name"
                type="text"
                value={lastName}
                placeholder="Например, Ромашка"
                autoFocus
                onChange={(event) => {
                  setLastName(event.target.value);
                  setCompanyError('');
                  setCompanySuccess('');
                }}
              />
            </FieldGroup>

            <Button type="submit" disabled={companySubmitting} margin="0">
              {companySubmitting ? 'Создаем...' : 'Создать компанию'}
            </Button>
          </Form>
        </SectionCard>

        <CompaniesDirectory
          authorization={authorization}
          createdCompany={createdCompany}
        />
      </SectionsGrid>
    </Paper>
  );
}

function CompaniesDirectory({authorization, createdCompany}) {
  const [companies, setCompanies] = React.useState([]);
  const [companiesLoading, setCompaniesLoading] = React.useState(true);
  const [companiesError, setCompaniesError] = React.useState('');
  const [expandedCompanyId, setExpandedCompanyId] = React.useState(null);
  const [usersByCompany, setUsersByCompany] = React.useState({});
  const [usersLoadingCompanyId, setUsersLoadingCompanyId] = React.useState(null);
  const [usersError, setUsersError] = React.useState('');
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [assignmentSubmitting, setAssignmentSubmitting] = React.useState(false);
  const [assignmentError, setAssignmentError] = React.useState('');
  const [assignmentSuccess, setAssignmentSuccess] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;

    setCompaniesLoading(true);
    setCompaniesError('');

    getAdminCompanies({authorization})
      .then(async response => {
        if ((response.status >= 200) && (response.status < 300)) {
          return response.json();
        }

        throw new Error(await getErrorMessage(response, 'Не удалось загрузить компании'));
      })
      .then((data) => {
        if (!cancelled) {
          const loadedCompanies = Array.isArray(data) ? data : [];
          setCompanies((currentCompanies) => [
            ...currentCompanies.filter(current => (
              !loadedCompanies.some(company => company.id === current.id)
            )),
            ...loadedCompanies,
          ]);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setCompaniesError(error.message || 'Не удалось загрузить компании');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCompaniesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authorization]);

  React.useEffect(() => {
    if (!createdCompany) {
      return;
    }

    setCompanies((items) => [
      createdCompany,
      ...items.filter(item => item.id !== createdCompany.id),
    ]);
    setUsersByCompany((items) => ({...items, [createdCompany.id]: []}));
    setExpandedCompanyId(createdCompany.id);
    setSelectedUser(null);
    setUsersError('');
    setAssignmentError('');
    setAssignmentSuccess('');
  }, [createdCompany]);

  const loadCompanyUsers = async (companyId) => {
    if (Object.prototype.hasOwnProperty.call(usersByCompany, companyId)) {
      return;
    }

    setUsersLoadingCompanyId(companyId);
    setUsersError('');

    try {
      const response = await getAdminCompanyUsers(companyId, {authorization});

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось загрузить пользователей компании'));
      }

      const users = await response.json();
      setUsersByCompany((items) => ({
        ...items,
        [companyId]: Array.isArray(users) ? users : [],
      }));
    } catch (error) {
      setUsersError(error.message || 'Не удалось загрузить пользователей компании');
    } finally {
      setUsersLoadingCompanyId(current => current === companyId ? null : current);
    }
  };

  const handleToggleCompany = (companyId) => {
    if (expandedCompanyId === companyId) {
      setExpandedCompanyId(null);
      return;
    }

    setExpandedCompanyId(companyId);
    setSelectedUser(null);
    setAssignmentError('');
    setAssignmentSuccess('');
    setUsersError('');
    loadCompanyUsers(companyId);
  };

  const handleAssignUser = async (event, company) => {
    event.preventDefault();

    if (!selectedUser) {
      setAssignmentError('Выберите пользователя');
      setAssignmentSuccess('');
      return;
    }

    setAssignmentSubmitting(true);
    setAssignmentError('');
    setAssignmentSuccess('');

    try {
      const response = await postAdminUserSharedAccount(
        selectedUser.id,
        {authorization},
        {shared_account_id: company.id}
      );

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось назначить корпоративный счет'));
      }

      const data = await response.json();
      const previousCompanyId = data.previous_shared_account_id;
      const assignedUser = {
        ...selectedUser,
        shared_account_id: company.id,
        shared_account: data.shared_account,
      };

      setUsersByCompany((items) => {
        const updated = {...items};
        const targetUsers = updated[company.id] || [];
        updated[company.id] = [
          assignedUser,
          ...targetUsers.filter(user => user.id !== assignedUser.id),
        ];

        if (previousCompanyId && previousCompanyId !== company.id && updated[previousCompanyId]) {
          updated[previousCompanyId] = updated[previousCompanyId]
            .filter(user => user.id !== assignedUser.id);
        }

        return updated;
      });

      if (previousCompanyId !== company.id) {
        setCompanies((items) => items.map(item => {
          if (item.id === company.id) {
            return {...item, users_count: Number(item.users_count || 0) + 1};
          }

          if (item.id === previousCompanyId) {
            return {...item, users_count: Math.max(Number(item.users_count || 0) - 1, 0)};
          }

          return item;
        }));
      }

      setAssignmentSuccess(
        `Пользователь «${formatAdminUserName(selectedUser)}» подключен к компании «${formatAdminUserName(company)}».`
      );
      setSelectedUser(null);
    } catch (error) {
      setAssignmentError(error.message || 'Не удалось назначить корпоративный счет');
    } finally {
      setAssignmentSubmitting(false);
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionNumber>2</SectionNumber>
        <div>
          <SectionTitle>Добавленные компании</SectionTitle>
          <SectionDescription>
            Раскройте компанию, чтобы посмотреть пользователей или подключить нового.
          </SectionDescription>
        </div>
      </SectionHeader>

      {companiesLoading && <StateBlock>Загружаем компании...</StateBlock>}
      {!companiesLoading && companiesError && <ErrorAlert noTriangle>{companiesError}</ErrorAlert>}
      {!companiesLoading && !companiesError && companies.length === 0 && (
        <StateBlock>Компании пока не добавлены.</StateBlock>
      )}

      {!companiesLoading && !companiesError && companies.length > 0 && (
        <CompaniesList>
          {companies.map(company => {
            const expanded = expandedCompanyId === company.id;
            const users = usersByCompany[company.id] || [];
            const usersLoading = usersLoadingCompanyId === company.id;

            return (
              <CompanyCard key={company.id}>
                <CompanyHeaderButton
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => handleToggleCompany(company.id)}
                >
                  <CompanyIdentity>
                    <CompanyName>{formatAdminUserName(company)}</CompanyName>
                    <CompanyEmail>{company.email || '—'}</CompanyEmail>
                  </CompanyIdentity>
                  <CompanyMeta>
                    <CompanyBadge>{Number(company.users_count || 0)} польз.</CompanyBadge>
                    <CompanyBalance>{formatAdminUserAmount(company.account_amount)} ₽</CompanyBalance>
                    <ExpandMark expanded={expanded}>⌄</ExpandMark>
                  </CompanyMeta>
                </CompanyHeaderButton>

                {expanded && (
                  <CompanyDetails>
                    {usersLoading && <StateBlock>Загружаем пользователей...</StateBlock>}
                    {!usersLoading && usersError && <ErrorAlert noTriangle>{usersError}</ErrorAlert>}
                    {!usersLoading && !usersError && users.length === 0 && (
                      <StateBlock>К корпоративному счету пока никто не подключен.</StateBlock>
                    )}
                    {!usersLoading && !usersError && users.length > 0 && (
                      <MembersList>
                        {users.map(user => (
                          <MemberRow key={user.id}>
                            <div>
                              <MemberName>{formatAdminUserName(user)}</MemberName>
                              <MemberMeta>{user.phone || user.email || '—'}</MemberMeta>
                            </div>
                            <MemberEmail>{user.email || '—'}</MemberEmail>
                          </MemberRow>
                        ))}
                      </MembersList>
                    )}

                    <AssignmentDivider />
                    <InlineForm onSubmit={(event) => handleAssignUser(event, company)}>
                      <InlineTitle>Подключить пользователя</InlineTitle>
                      {assignmentError && <ErrorAlert noTriangle>{assignmentError}</ErrorAlert>}
                      {assignmentSuccess && <SuccessAlert>{assignmentSuccess}</SuccessAlert>}

                      <AdminUserPicker
                        authorization={authorization}
                        selectedUser={selectedUser}
                        excludeRoleFilter="company"
                        label="Пользователь"
                        searchTitle="Найти пользователя"
                        searchDescription="Компании исключены из результатов. Поиск начинается после 3 символов."
                        onSelect={(user) => {
                          setSelectedUser(user);
                          setAssignmentError('');
                          setAssignmentSuccess('');
                        }}
                      />

                      {selectedUser && selectedUser.shared_account
                        && selectedUser.shared_account.id !== company.id && (
                        <TransferNotice>
                          Пользователь будет перенесен из компании «{formatAdminUserName(selectedUser.shared_account)}».
                        </TransferNotice>
                      )}

                      <Button
                        type="submit"
                        disabled={assignmentSubmitting || !selectedUser}
                        margin="0"
                      >
                        {assignmentSubmitting ? 'Подключаем...' : 'Подключить к этой компании'}
                      </Button>
                    </InlineForm>
                  </CompanyDetails>
                )}
              </CompanyCard>
            );
          })}
        </CompaniesList>
      )}
    </SectionCard>
  );
}

const PageHeader = styled.div`
  margin-bottom: 28px;
`;

const Heading = styled.h2`
  margin: 0 0 8px;
  font-size: 22px;
  line-height: 1.2;
  color: ${colors.TEXT_TITLE};
`;

const Description = styled.div`
  max-width: 760px;
  color: ${colors.TEXT_MINOR};
  font-size: 14px;
  line-height: 1.6;
`;

const Code = styled.span`
  padding: 2px 6px;
  border-radius: 6px;
  background: ${colors.BG};
  color: ${colors.TEXT_MAIN};
  font-family: monospace;
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 24px;

  @media screen and (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled.section`
  padding: 24px;
  border: 1px solid ${colors.LINE};
  border-radius: 14px;
  background: white;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 24px;
`;

const SectionNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${colors.BLUE_LIGHT};
  color: ${colors.BLUE_MAIN};
  font-size: 14px;
  font-weight: bold;
`;

const SectionTitle = styled.h3`
  margin: 2px 0 5px;
  color: ${colors.TEXT_TITLE};
  font-size: 18px;
  line-height: 1.3;
`;

const SectionDescription = styled.div`
  color: ${colors.TEXT_MINOR};
  font-size: 13px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  color: ${colors.TEXT_MAIN};
  font-size: 14px;
  font-weight: bold;
`;

const FieldInput = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${colors.INPUT_STROKE};
  border-radius: 8px;
  outline: none;
  background: white;
  color: ${colors.TEXT_MAIN};
  font-size: 15px;

  &:focus {
    border-color: ${colors.BUTTON_BLUE};
    box-shadow: 0 0 0 3px rgba(71, 146, 255, 0.12);
  }
`;

const HelperText = styled.div`
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  line-height: 1.5;
`;

const SuccessAlert = styled.div`
  padding: 14px 16px;
  border: 1px solid #D8ECCA;
  border-radius: 8px;
  background: #F4FBEE;
  color: ${colors.GREEN_DARK};
  font-size: 14px;
  line-height: 1.5;
`;

const StateBlock = styled.div`
  padding: 16px;
  border-radius: 10px;
  background: ${colors.BG};
  color: ${colors.TEXT_MINOR};
  font-size: 14px;
  line-height: 1.5;
`;

const CompaniesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CompanyCard = styled.div`
  overflow: hidden;
  border: 1px solid ${colors.LINE};
  border-radius: 12px;
  background: white;
`;

const CompanyHeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 16px;
  border: 0;
  background: white;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus {
    background: ${colors.BLUE_LIGHT};
    outline: none;
  }
`;

const CompanyIdentity = styled.div`
  min-width: 0;
`;

const CompanyName = styled.div`
  overflow: hidden;
  color: ${colors.TEXT_TITLE};
  font-size: 15px;
  font-weight: bold;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CompanyEmail = styled.div`
  margin-top: 3px;
  overflow: hidden;
  color: ${colors.TEXT_MINOR};
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CompanyMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const CompanyBadge = styled.span`
  padding: 5px 9px;
  border-radius: 999px;
  background: ${colors.BG};
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  white-space: nowrap;
`;

const CompanyBalance = styled.span`
  color: ${colors.BLUE_MAIN};
  font-size: 13px;
  font-weight: bold;
  white-space: nowrap;
`;

const ExpandMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${colors.TEXT_MINOR};
  font-size: 20px;
  transform: rotate(${props => props.expanded ? '180deg' : '0deg'});
  transition: transform 0.2s ease;
`;

const CompanyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid ${colors.LINE};
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.LINE};
  border-radius: 10px;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-bottom: 1px solid ${colors.LINE};

  &:last-child {
    border-bottom: 0;
  }
`;

const MemberName = styled.div`
  color: ${colors.TEXT_MAIN};
  font-size: 14px;
  font-weight: bold;
`;

const MemberMeta = styled.div`
  margin-top: 3px;
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
`;

const MemberEmail = styled.div`
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  text-align: right;
`;

const AssignmentDivider = styled.div`
  height: 1px;
  margin: 2px 0;
  background: ${colors.LINE};
`;

const InlineForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InlineTitle = styled.h4`
  margin: 0;
  color: ${colors.TEXT_TITLE};
  font-size: 15px;
`;

const TransferNotice = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: #FFF8E6;
  color: ${colors.TEXT_MAIN};
  font-size: 13px;
  line-height: 1.5;
`;

SharedAccountsTab.propTypes = {
  authorization: PropTypes.string,
};

SharedAccountsTab.defaultProps = {
  authorization: '',
};

CompaniesDirectory.propTypes = {
  authorization: PropTypes.string,
  createdCompany: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    account_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    users_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

CompaniesDirectory.defaultProps = {
  authorization: '',
  createdCompany: null,
};

export default SharedAccountsTab;
